import React, { Component } from 'react';
import { compose } from '../../reactApolloCompat';
import { withRouter } from '../../util/withRouter';
import withStyles from '@mui/styles/withStyles';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '../icon/SearchIcon';
import TextField from '@mui/material/TextField';
import Spinner from '../Spinner';
import Paper from '@mui/material/Paper';
import Downshift from 'downshift';
import SearchQuery from '../query/SearchQuery';
import HeaderSearchResults from './HeaderSearchResults';
import { SearchTermConsumer, withSearchTerm } from '../../context/searchTerm';
import cx from 'classnames';
import debounce from 'lodash.debounce';
import GatedComponent from '../GatedComponent';
import { getDownshiftInputProps } from '../../util/functions';

class HeaderSearchInput extends Component {
    state = {
        focused: false,
        isOnSearchPage: false,
        isPrearrangements: false,
        highlightedPath: null
    };

    componentDidMount() {
        this.unlisten = this.props.history.listen(debounce(this.onUpdatePage, 100));
        this.onUpdatePage(this.props.location);
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.open && this.props.open && this.input) this.input.focus();
    }

    componentWillUnmount() {
        this.unlisten();
    }

    onUpdatePage = location => {
        const isOnSearchPage = location.pathname.substr(0, 7) === '/search';
        if (this.state.isOnSearchPage !== isOnSearchPage) {
            this.setState({ isOnSearchPage });
        }

        const isPrearrangements = location.pathname.includes('prearrangements');
        if (this.state.isPrearrangements !== isPrearrangements) {
            this.setState({ isPrearrangements });
        }
    };

    onInputRef = input => {
        this.input = input;
    };

    onInputFocus = () => {
        this.setState({ focused: true });
    };

    onInputBlur = () => {
        this.setState({ focused: false });
        this.input.blur();
    };

    onInputEnter = item => {
        const { history } = this.props;
        const { highlightedPath, isOnSearchPage } = this.state;
        const { ID, LegacyKey } = item || {};
        if (ID > 0) {
            history.push(`/funeral/${LegacyKey}/${ID}`);
        } else if (highlightedPath) {
            history.push(highlightedPath);
        } else if (!isOnSearchPage) {
            history.push('/search');
        }
        this.onInputBlur();
    };

    onItemHighlighted = item => {
        const { highlightedPath } = this.state;
        if (highlightedPath !== item) this.setState({ highlightedPath: item });
    };

    onItemSelected = term => {
        this.input.blur();
        this.props.setSearchTerm(term);
    };

    onSubmit = e => {
        e.preventDefault();
        if (!this.state.isOnSearchPage) {
            this.props.history.push('/search');
        }
    };

    render() {
        return <SearchTermConsumer>{this.renderQuery}</SearchTermConsumer>;
    }

    renderQuery = ({ searchTerm }) => {
        const { focused, isOnSearchPage } = this.state;
        const sortBy = [{ field: 'LastEdited', direction: 'DESC' }];

        const highlightHelper = (state, changes) => {
            switch (changes.type) {
                case Downshift.stateChangeTypes.keyDownArrowDown:
                    if (!state.isOpen) {
                        return { ...changes, highlightedIndex: 0 };
                    }
                    return changes;
                case Downshift.stateChangeTypes.keyDownArrowUp:
                    if (state.highlightedIndex === 0) {
                        this.setState({ highlightedPath: null });
                        return { ...changes, highlightedIndex: null };
                    }
                    return changes;
                case Downshift.stateChangeTypes.changeInput:
                    return { ...changes, highlightedIndex: null };
                case Downshift.stateChangeTypes.keyDownEscape:
                case Downshift.stateChangeTypes.keyDownEnter:
                case Downshift.stateChangeTypes.clickItem:
                case Downshift.stateChangeTypes.blurInput:
                    return {
                        ...changes,
                        highlightedIndex: null,
                        isOpen: false
                    };
                default:
                    return changes;
            }
        };
        return (
            <SearchQuery sortBy={sortBy} term={searchTerm} skip={!focused || isOnSearchPage} oneLoad>
                {({ term, resultsTerm, results, totalCount }) => (
                    <Downshift stateReducer={highlightHelper}>
                        {downshiftData => this.renderContainer(term, resultsTerm, results, downshiftData, totalCount)}
                    </Downshift>
                )}
            </SearchQuery>
        );
    };

    renderContainer(term, resultsTerm, results, downshiftData, totalCount) {
        const { classes, className } = this.props;
        const { focused, isOnSearchPage } = this.state;
        const elevated = isOnSearchPage || focused;
        return (
            <div className={cx(classes.root, className)}>
                <Paper className={cx(elevated ? classes.paperElevated : classes.paper)} elevation={elevated ? 1 : 0}>
                    {this.renderInput(downshiftData, !isOnSearchPage && term && term !== resultsTerm, results)}
                </Paper>
                {focused && (
                    <HeaderSearchResults
                        term={term}
                        resultsTerm={resultsTerm}
                        results={results}
                        downshiftData={downshiftData}
                        onItemHighlighted={this.onItemHighlighted}
                        onItemSelected={this.onItemSelected}
                        totalCount={totalCount}
                    />
                )}
            </div>
        );
    }

    renderInput(downshiftData, loading, results) {
        const { classes, searchTerm, setSearchTerm } = this.props;
        const { isPrearrangements, focused } = this.state;
        const highlighted = !!results && results[downshiftData.highlightedIndex];
        const downshiftInputProps = getDownshiftInputProps(downshiftData.getInputProps);
        const downshiftOnKeyDown = downshiftInputProps().onKeyDown;
        const downshiftOnChange = downshiftInputProps().onChange;
        // This is to shut up linting errors because it's case insensitive
        const otherProps = {
            inputProps: {
                ref: this.onInputRef,
                tabIndex: 0,
                onChange: evt => {
                    downshiftOnChange(evt);
                    setSearchTerm(evt.target.value);
                },
                value: searchTerm,
                onFocus: this.onInputFocus,
                onBlur: this.onInputBlur,
                onKeyDown: evt => {
                    downshiftOnKeyDown(evt);
                    'Enter' === evt.key && this.onInputEnter(highlighted);
                }
            }
        };

        return (
            <GatedComponent showComponentCode={'FM_ACCESS_Funeral_View'}>
                {() => {
                    return (
                        <form onSubmit={this.onSubmit}>
                            <TextField
                                style={{ marginBottom: 0 }}
                                fullWidth
                                type="search"
                                {...otherProps}
                                InputProps={{
                                    disableUnderline: true,
                                    startAdornment: (
                                        <InputAdornment position="start" className={classes.iconContainer}>
                                            {focused && loading ? <Spinner /> : <SearchIcon className={classes.icon} />}
                                        </InputAdornment>
                                    ),
                                    ...downshiftInputProps({
                                        placeholder:
                                            'Search ' +
                                            (!isPrearrangements ? 'funerals' : 'prearrangements') +
                                            ' by name or key...'
                                    })
                                }}
                            />
                        </form>
                    );
                }}
            </GatedComponent>
        );
    }
}

const styles = ({ palette }) => ({
    root: {
        position: 'relative',
        maxWidth: 600
    },
    paper: {
        padding: 8,
        border: '1px solid transparent!important'
    },
    paperElevated: {
        padding: 8,
        border: '1px solid #d5d5d5'
    },
    iconContainer: {
        alignSelf: 'center'
    },
    icon: {
        fontSize: 16,
        color: palette.basePrimary['none']
    }
});

// prettier-ignore
export default compose(
    withSearchTerm,
    withRouter,
    withStyles(styles)
)(HeaderSearchInput);
